import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { fmtInTz } from "@/lib/utils";

export const RANGES = ["24h", "7d", "30d"];

export function RangeTabs({ value, onChange, prefix = "range" }) {
  return (
    <div className="glass flex rounded-2xl p-1">
      {RANGES.map((r) => (
        <button key={r} onClick={() => onChange(r)} data-testid={`${prefix}-${r}`}
                className="pressable flex-1 rounded-xl px-4 py-1.5 text-xs font-bold"
                style={{ background: value === r ? "linear-gradient(135deg,#F59E0B,#D97706)" : "transparent",
                         color: value === r ? "#fff" : "var(--text-dim)" }}>{r.toUpperCase()}</button>
      ))}
    </div>
  );
}

export default function PriceHistoryChart({ symbol }) {
  const [range, setRange] = useState("7d");
  const { currency, convert } = useCurrency();
  const { user } = useAuth();
  const tz = user?.timezone || "UTC";
  const { data, isLoading } = useQuery({
    queryKey: ["history", symbol, range],
    queryFn: async () => (await api.get(`/prices/${symbol}/history`, { params: { range } })).data.points,
  });
  const pts = (data || []).map((p) => ({ t: p.t, v: convert(p.price, "USD", currency) }));
  const up = pts.length < 2 || pts[pts.length - 1].v >= pts[0].v;
  const color = up ? "#34D07F" : "#FF5C5C";
  const tick = (t) => fmtInTz(t, tz, range === "24h" ? { hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short" }).replace(/ \w+$/, "");

  return (
    <div data-testid="price-history-chart">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-dim">Price history</p>
        <RangeTabs value={range} onChange={setRange} />
      </div>
      <div className="goe-card p-3" style={{ height: 260 }}>
        {isLoading ? <div className="skeleton h-full w-full" /> : pts.length < 2 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-dim" data-testid="history-empty">
            Not enough history yet for this range — it builds with every twice-daily price update.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={pts} margin={{ top: 10, right: 6, left: 6, bottom: 0 }}>
              <defs>
                <linearGradient id={`g-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} tickFormatter={tick}
                     tick={{ fontSize: 10, fill: "var(--text-dim)" }} axisLine={false} tickLine={false} minTickGap={40} />
              <YAxis domain={["dataMin", "dataMax"]} hide />
              <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--text)" }}
                       labelFormatter={(t) => fmtInTz(t, tz)}
                       formatter={(v) => [v.toLocaleString(undefined, { maximumFractionDigits: 2 }), currency]} />
              <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2.5} fill={`url(#g-${symbol})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
